import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material"
import {
  DeleteOutlined,
  MoreVertOutlined,
  VerticalAlignBottomOutlined,
  VisibilityOutlined,
} from "@mui-symbols-material/w300"
import type { CellContext } from "@tanstack/react-table"
import { type FC, useRef, useState } from "react"

import type { SignedUrl } from "@/graphql/codegen/graphql"
import { useFileDownload } from "@/hooks/useFileDownload"

interface ProductTestDocumentRowActionsProps {
  row: CellContext<SignedUrl, unknown>["row"]
  canDownload?: boolean
  canDelete?: boolean
  onDelete: (document: SignedUrl) => void
}

export const ProductTestDocumentRowActions: FC<ProductTestDocumentRowActionsProps> = ({
  row,
  canDownload = true,
  canDelete = true,
  onDelete,
}) => {
  const { downloadFile } = useFileDownload()
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const handleDropdownOpen = () => setIsDropdownOpen(true)

  const handleDropdownClose = () => setIsDropdownOpen(false)

  const handlePreview = (signedUrl: SignedUrl) => {
    handleDropdownClose()
    window.open(signedUrl.url, "_blank")
  }

  const handleDownload = async (signedUrl: SignedUrl) => {
    handleDropdownClose()
    const { url, file } = signedUrl

    if (!file) return

    await downloadFile(url, file.originalName!)
  }

  const handleDelete = (signedUrl: SignedUrl) => {
    handleDropdownClose()
    onDelete(signedUrl)
  }

  return (
    <Box className='flex items-center justify-end' ref={anchorElRef}>
      <IconButton onClick={handleDropdownOpen} data-test='row-actions-button'>
        <MoreVertOutlined />
      </IconButton>
      <Menu
        anchorEl={anchorElRef.current}
        open={isDropdownOpen}
        onClose={handleDropdownClose}
        data-test='row-actions-menu'
      >
        <MenuItem onClick={() => handlePreview(row.original)}>
          <ListItemIcon>
            <VisibilityOutlined sx={{ fontVariationSettings: '"wght" 300' }} />
          </ListItemIcon>
          <ListItemText primary='View Document' />
        </MenuItem>
        {canDownload && (
          <MenuItem onClick={() => handleDownload(row.original)}>
            <ListItemIcon>
              <VerticalAlignBottomOutlined sx={{ fontVariationSettings: '"wght" 300' }} />
            </ListItemIcon>
            <ListItemText primary='Download Document' />
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem onClick={() => handleDelete(row.original)}>
            <ListItemIcon>
              <DeleteOutlined sx={{ fontVariationSettings: '"wght" 300' }} />
            </ListItemIcon>
            <ListItemText primary='Delete Document' />
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}
